import Swal from "sweetalert";
import MasterApi from "src/api/masterApi";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ResponseError from "src/views/kalasanPages/modal/responseError";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  handleShowAdd,
  handleShowEdit,
} from "src/configs/redux/action/modalAction";
import {
  hideLoading,
  showLoading,
} from "src/utils/globalComponents/SwalLoadingComponent";

export const useGetCustomers = (
  dispatch,
  history,
  currentPage,
  projectPerPage,
  field,
  sort,
  search,
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["customers", currentPage, projectPerPage, field, sort, search],
    async () => {
      const res = await MasterApi.getCustomers({
        token,
        currentPage,
        projectPerPage,
        field,
        sort,
        search,
      });
      return res;
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetCustomerById = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["customer", id],
    async () => {
      const res = await MasterApi.getCustomerById({
        token,
        id,
      });
      return res.data;
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useVerifyPin = (dispatch, history) => {
  return useMutation(MasterApi.verifyPin, {
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useCreateCustomer = (dispatch, history) => {
  const queryClient = useQueryClient();
  return useMutation(MasterApi.createCustomer, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["customers"]);
      Swal("Customer Baru berhasil ditambahkan!", "", "success");
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useEditCustomer = (dispatch, history) => {
  const queryClient = useQueryClient();
  return useMutation(MasterApi.editCustomer, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["customers"]);
      Swal("Customer berhasil diubah!", "", "success");
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useDeleteCustomer = (dispatch, history) => {
  const queryClient = useQueryClient();
  return useMutation(MasterApi.deleteCustomer, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["customers"]);
      Swal("Customer berhasil dihapus!", "", "success");
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetCities = (dispatch, history, idProvinsi, visible) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["cities", idProvinsi],
    async () => {
      const res = await MasterApi.getCities({
        token,
        idProvinsi,
      });

      // todo : handle map response on backend
      const response = res.data?.map((v) => ({
        label: v.city_name,
        value: v.city_id,
      }));
      return response ?? []; // important always fallback to empty array
    },
    {
      enabled: visible ?? false,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetProvinces = (dispatch, history, visible) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["provinces"],
    async () => {
      const res = await MasterApi.getProvinces({
        token,
      });
      // todo : handle map response on backend
      const response = res.data?.map((v) => ({
        value: v.prov_id,
        label: v.prov_name,
      }));
      return response ?? []; // important always fallback to empty array
    },
    {
      enabled: visible ?? false,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetMasterData = (dispatch, history, type) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["get-satuan", type],
    async () => {
      const res = await MasterApi.getMasterData({
        token,
        type,
      });
      return res.data ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useUpdateMasterData = (dispatch, history, refetch) => {
  return useMutation(MasterApi.updateMasterData, {
    onSuccess: () => {
      Swal("Berhasil ubah master data!", "", "success");
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetEmployeeByRole = ({
  dispatch,
  history,
  outlet_id,
  role,
  enabled,
  url,
  params,
}) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["employee-dropdown", outlet_id, role, url, params],
    async () => {
      const res = await MasterApi.getByRole({
        token,
        outlet_id,
        role,
        url,
        ...params,
      });
      const raw = res?.data?.map((item) => ({
        label: item.name,
        value: item.employee_id,
        division_id: item.division_id,
      }));
      return raw ?? [];
    },
    {
      enabled: enabled,
      onError: (err) => {
        ResponseError(err, dispatch, history);
      },
    },
  );
};

export const useGetProductDropdown = (
  dispatch,
  history,
  params = {},
  option = {},
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["product-dropdown", params],
    async () => {
      const res = await MasterApi.productDropdown({
        token,
        params,
      });
      const response = res?.data?.map((item) => ({
        ...item,
        value: item.value,
        label: item.label,
        units: item.units,
        is_production: item.is_production,
        division_id: item.division_id,
      }));

      return response ?? [];
    },
    {
      ...option,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAssetDropdown = (dispatch, history, params = {}) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["asset-dropdown", params],
    async () => {
      const res = await MasterApi.assetDropdown({
        token,
        ...params,
      });

      return res.data ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetProductDropdownByWarehouse = (
  dispatch,
  history,
  params = {},
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["product-dropdown-by-warehouse", params],
    async () => {
      const res = await MasterApi.productDropdownByWarehouse({
        token,
        ...params,
      });

      return res?.data ?? [];
    },
    {
      enabled: Boolean(params?.warehouse_id),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetProductDropdownForSPK = (
  dispatch,
  history,
  visible,
  category,
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["product-dropdown-for-spk"],
    async () => {
      const res = await MasterApi.getAllProductsOptionForSPK({
        token,
        category,
      });

      const raw = res.data.map((item) => ({
        value: item.value,
        label: item.label,
      }));

      return raw;
    },
    {
      enabled: Boolean(visible),
      onError: (err) => {
        ResponseError(err, dispatch, history);
      },
    },
  );
};

export const useGetProductHaveParent = (dispatch, history, option = {}) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["product-dropdown"],
    async () => {
      const res = await MasterApi.productHaveParent({
        token,
      });

      const response = res?.data.map((item) => ({
        ...item,
        value: item.product_id,
        label: item.product_name,
      }));

      return response ?? [];
    },
    {
      ...option,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetProductFastFoodOption = () => {
  const token = useSelector((state) => state.user.profile.token);
  const dispatch = useDispatch();
  const history = useHistory();

  return useQuery(
    ["product-fastfood-dropdown"],
    async () => {
      const res = await MasterApi.getProductFastFoodOptions({
        token,
      });
      const response = res?.data?.map((item) => ({
        ...item,
        value: item.product_id,
        label: item.name,
      }));

      return response ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetProductByWarehouse = (params = {}, isNewProduct = null) => {
  const token = useSelector((state) => state.user.profile.token);
  const dispatch = useDispatch();
  const history = useHistory();
  return useQuery(
    ["product-warehouse-dropdown", params],
    async () => {
      const res = await MasterApi.getProductByWarehouse({
        token,
        warehouse_id: params.warehouse_id,
      });
      const response = res?.data?.map((item) => ({
        ...item,
        value: item.product_id,
        label: item.product_name,
      }));

      return response ?? [];
    },
    {
      enabled:
        !!params.warehouse_id &&
        (isNewProduct === null || isNewProduct === true),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAssetByWarehouse = (dispatch, history, params = {}) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["asset-warehouse-dropdown"],
    async () => {
      const res = await MasterApi.getAssetByWarehouse({
        token,
        outlet_id: params.outlet_id,
      });
      const response = res?.data?.map((item) => ({
        ...item,
        value: item.asset_id,
        label: item.asset_name,
      }));

      return response ?? [];
    },
    {
      enabled: !!params.outlet_id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetSupplier = (dispatch, history, productId, isBarangPajak) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    [`supplier-data-${productId}`],
    async () => {
      const res = await MasterApi.getAllSupplier({
        token,
        productId,
        isBarangPajak,
      });

      const raw = res.data.map((item) => ({
        value: item.supplier_id,
        label: item.name,
      }));
      raw.sort((a, b) => a.label.localeCompare(b.label));
      return raw ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAllProduct = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["all-product"],
    async () => {
      const res = await MasterApi.getAllProducts({
        token,
      });

      const newOption = res.data.map((item) => {
        let unitOpt = [];

        if (item.unit1_id) {
          unitOpt.push({
            value: item.unit1_id,
            label: item.unit1,
            conversion: 1,
          });
        }

        if (item.unit2_id) {
          unitOpt.push({
            value: item.unit2_id,
            label: item.unit2,
            conversion: item.conversion_1,
          });
        }

        if (item.unit3_id) {
          unitOpt.push({
            value: item.unit3_id,
            label: item.unit3,
            conversion: item.conversion_2,
          });
        }

        return {
          value: item.product_id,
          label: item.name,
          unit_option: unitOpt,
        };
      });
      return newOption ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetProductionBom = (dispatch, history, params, data) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["all-production-bom", params, data],
    async () => {
      return await MasterApi.getAllProductionBOM({
        token,
        params,
      });
    },
    {
      enabled: !!data,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};
export const useGetPoolOptions = (
  dispatch,
  history,
  status = "AVAILABLE",
  enabled,
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["pool-options"],
    async () => {
      const res = await MasterApi.getPoolOptions({
        token,
        status,
      });
      return res.data ?? [];
    },
    {
      enabled: enabled,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetOutletOptions = (dispatch, history, enabled = true) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["outlet-options"],
    async () => {
      const res = await MasterApi.getOutletOptions({
        token,
      });
      const response = res.data.map((item) => ({
        value: item.outlet_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      enabled: enabled,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetWarehouseNonPaginated = (
  dispatch,
  history,
  temporary = "",
  is_pic = true,
  is_retur = false,
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["all-warehouse"],
    async () => {
      const res = await MasterApi.getAllWarehouseNoPagination({
        token,
        is_pic,
        temporary,
        is_retur,
      });

      return res?.data ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetWarehouseByNip = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["warehouse-by-nip"],
    async () => {
      const res = await MasterApi.getWarehouseByNip({ token });
      return res?.data || [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetWarehouseByOutlet = (dispatch, history, params) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["warehouse-by-outlet", params?.outlet_id],
    async () => {
      const res = await MasterApi.getWarehouseByOutlet({
        token,
        outlet_id: params?.outlet_id,
      });
      const response = res.data.map((item) => ({
        value: item.warehouse_id,
        label: item.name,
        outlet_id: item.outlet_id,
      }));
      return response ?? [];
    },
    {
      enabled: params?.enabled ?? true,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetWarehouseByCategory = (dispatch, history, categoryId) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["warehouse-by-category", categoryId],
    async () => {
      const res = await MasterApi.getWarehouseByCategory({
        token,
        categoryId,
      });
      const response = res.data.map((item) => ({
        value: item.warehouse_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      enabled: !!categoryId,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetWarehouseByStock = (dispatch, history, productId) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["warehouse-by-category", productId],
    async () => {
      const res = await MasterApi.getWarehouseByCategory({
        token,
        productId,
      });
      const response = res.data.map((item) => ({
        value: item.warehouse_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      enabled: !!productId,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetWarehouseByConfigWarehouse = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["warehouse-by-config-warehouse", id],
    async () => {
      const res = await MasterApi.getWarehouseByConfigWarehouse({
        token,
        id,
      });

      return res.data ?? [];
    },
    {
      enabled: Boolean(id),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetMasterProductById = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["master-product", id],
    async () => {
      const res = await MasterApi.getMasterProductById({
        token,
        id,
      });
      return res.data[0] ?? {};
    },
    {
      enabled: !!id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useImportProductWarehouse = (dispatch, history, refetch) => {
  return useMutation(MasterApi.importProductWarehouse, {
    onSuccess: () => {
      Swal("Berhasil import data produk gudang!", "", "success");
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetMasterProductDetail = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["master-product-detail", id],
    async () => {
      const res = await MasterApi.getMasterProductDetail({
        token,
        id,
      });
      return res.data[0] ?? {};
    },
    {
      enabled: !!id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetMasterProductChild = (id) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["master-product-child", id],
    async () => {
      const res = await MasterApi.getMasterProductChild({
        token,
        id,
      });
      return res.data ?? {};
    },
    {
      enabled: !!id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useCreateMasterProduct = (
  dispatch,
  history,
  refetch,
  refetchProductParent,
) => {
  return useMutation(MasterApi.createMasterProduct, {
    onSuccess: () => {
      Swal("Produk baru berhasil ditambahkan!", "", "success");
      refetch();
      refetchProductParent();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useUpdateMasterProduct = (
  dispatch,
  history,
  refetch,
  setVisible,
) => {
  return useMutation(MasterApi.updateMasterProduct, {
    onMutate: () => showLoading(),
    onSuccess: (res) => {
      Swal("Produk berhasil diperbarui!", "", "success");
      refetch();
      hideLoading();
      setVisible(false);
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetProductSpec = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["get-product-spec", id],
    async () => {
      return await MasterApi.getProductSpecById({
        token,
        id,
      });
    },
    {
      enabled: !!id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useCreateProductSpec = (dispatch, history, refetch) => {
  return useMutation(MasterApi.createProductSpec, {
    onSuccess: (res) => {
      Swal("Berhasil membuat spesifikasi produk baru", "", "success");
      refetch();
    },
    onError: (err) => {
      Swal("Gagal Print Barcode Produk!", "", "error");
      ResponseError(err, dispatch, history);
    },
  });
};

export const useUpdateProductSpec = (dispatch, history, refetch) => {
  return useMutation(MasterApi.updateProductSpec, {
    onSuccess: (res) => {
      Swal("Berhasil mengubah data spesifikasi produk", "", "success");
      refetch();
    },
    onError: (err) => {
      Swal("Gagal Print Barcode Produk!", "", "error");
      ResponseError(err, dispatch, history);
    },
  });
};

export const useDeleteProductSpec = (dispatch, history, refetch) => {
  return useMutation(MasterApi.deleteProductSpec, {
    onSuccess: (res) => {
      Swal("Berhasil menghapus data spesifikasi produk", "", "success");
      refetch();
    },
    onError: (err) => {
      Swal("Gagal Print Barcode Produk!", "", "error");
      ResponseError(err, dispatch, history);
    },
  });
};

// MASTER - PEGAWAI
export const useGetAllEmployees = (dispatch, history, params) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["employees", params],
    async () => {
      const res = await MasterApi.getEmployees({
        token,
        params,
      });
      return res ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};
export const useDeleteEmployee = (dispatch, history, refetch) => {
  return useMutation(MasterApi.deleteEmployee, {
    onSuccess: (res) => {
      Swal("Status pegawai berhasil diubah menjadi resign!", "", "success");
      refetch();
    },
    onError: (err) => {
      Swal("Gagal mengubah status pegawai menjadi resign!", "", "error");
      ResponseError(err, dispatch, history);
    },
  });
};
export const useGetAllRole = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["roles-options"],
    async () => {
      const raw = await MasterApi.getRoles({
        token,
      });
      const response = raw.data.map((item) => ({
        value: item.role_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetPositionOptions = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["positions-options"],
    async () => {
      const raw = await MasterApi.getPositions({
        token,
      });
      const response = raw.data.map((item) => ({
        value: item.position_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetDivisionOptions = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["divisions-options"],
    async () => {
      const raw = await MasterApi.getDivisions({
        token,
      });
      const response = raw.data.map((item) => ({
        value: item.division_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetSubDivisionOptions = (dispatch, history, params) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["sub-divisions-options", params],
    async () => {
      const raw = await MasterApi.getSubDivisions({
        token,
        divisionId: params.division_id,
        outletId: params.outlet_id,
      });
      const response = raw.data.map((item) => ({
        value: item.division_sub_id,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      enabled: !!params.division_id && !!params.outlet_id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetEmployeeStatus = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["employees-status"],
    async () => {
      const raw = await MasterApi.getEmployeeStatus({
        token,
      });
      const response = raw.data.map((item) => ({
        value: item,
        label: item,
      }));
      return response ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetBankOptions = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["bank-options"],
    async () => {
      const raw = await MasterApi.getBankOptions({
        token,
      });
      const response = raw.data.map((item) => ({
        value: item.name,
        label: item.name,
      }));
      return response ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useCreateEmployee = (dispatch, history, refetch) => {
  const queryClient = useQueryClient();
  return useMutation(MasterApi.createEmployee, {
    onSuccess: (res) => {
      Swal("Data pegawai berhasil ditambahkan!", "", "success");
      queryClient.invalidateQueries(["employees"]);
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useUpdateEmployee = (dispatch, history, refetch) => {
  const queryClient = useQueryClient();
  return useMutation(MasterApi.updateEmployee, {
    onSuccess: (res) => {
      Swal("Data pegawai berhasil diubah!", "", "success");
      queryClient.invalidateQueries(["employees"]);
      refetch();
      dispatch(handleShowEdit(false));
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

// export const useGetEmployeeById = (dispatch, history, id) => {
//   const token = useSelector((state) => state.user.profile.token);
//   return useQuery(
//     ["employee", id],
//     async () => {
//       const res = await MasterApi.getEmployeeById({
//         token,
//         id,
//       });
//       return res.data;
//     },
//     {
//       onError: (err) => ResponseError(err, dispatch, history),
//     }
//   );
// };

export const useGetEmployeeById = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["employee", id],
    async () => {
      const res = await MasterApi.getEmployeeById({ token, id });

      return res?.data[0] ?? null;
    },
    {
      enabled: !!id,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAllUnusedRack = () => {
  const token = useSelector((state) => state.user.profile.token);
  const dispatch = useDispatch();
  const history = useHistory();

  return useQuery(
    ["unused-rack"],
    async () => {
      const res = await MasterApi.getAllUnusedRackOption({ token });

      return res?.data ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAllAccount = (params) => {
  const token = useSelector((state) => state.user.profile.token);
  const dispatch = useDispatch();
  const history = useHistory();

  return useQuery(
    ["all-account", params],
    async () => {
      const res = await MasterApi.getAllAccount({ token, params });

      return res;
    },
    {
      onError: (err) => {
        ResponseError(err, dispatch, history);
      },
    },
  );
};

export const useGetAllAccountOptions = (visible, params) => {
  const token = useSelector((state) => state.user.profile.token);
  const dispatch = useDispatch();
  const history = useHistory();

  return useQuery(
    ["all-account-option", params],
    async () => {
      const res = await MasterApi.getAllAccountOption({ token, ...params });

      return res;
    },
    {
      enabled: Boolean(visible),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAccountById = (id) => {
  const token = useSelector((state) => state.user.profile.token);
  const dispatch = useDispatch();
  const history = useHistory();

  return useQuery(
    ["account-by-id"],
    async () => {
      const res = await MasterApi.getAccountById({ token, id });

      return res;
    },
    {
      enabled: Boolean(id),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useCreateAccount = (dispatch, history, refetch) => {
  return useMutation(MasterApi.createAccount, {
    onSuccess: () => {
      Swal("Data akun berhasil ditambahkan!", "", "success");
      refetch();
      dispatch(handleShowAdd(false));
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useUpdateAccount = (refetch) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();

  return useMutation(MasterApi.updateAccount, {
    onSuccess: (res) => {
      Swal("Data akun berhasil diubah!", "", "success");
      queryClient.invalidateQueries(["all-account"]);
      refetch();
      dispatch(handleShowEdit(false));
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useDeleteAccount = (refetch) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();

  return useMutation(MasterApi.deleteAccount, {
    onSuccess: (res) => {
      Swal("Data akun berhasil dihapus!", "", "success");
      queryClient.invalidateQueries(["all-account"]);
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetShiftOptions = (dispatch, history, enabled, outlet_id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["shift-options", outlet_id],
    async () => {
      const res = await MasterApi.getShiftOptions({
        token,
        outlet_id,
      });

      return res?.data ?? [];
    },
    {
      enabled: enabled,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useCreateShift = (dispatch, history, refetch) => {
  return useMutation(MasterApi.createShift, {
    onSuccess: () => {
      Swal("Berhasil membuat data shift", "", "success");
      dispatch(handleShowAdd(false));
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useEditShift = (dispatch, history, refetch) => {
  return useMutation(MasterApi.editShift, {
    onSuccess: () => {
      Swal("Berhasil mengubah data shift", "", "success");
      dispatch(handleShowEdit(false));
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useDeleteShift = (dispatch, history, refetch) => {
  return useMutation(MasterApi.deleteShift, {
    onSuccess: () => {
      Swal("Berhasil menghapus data shift", "", "success");
      refetch();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetMasterShift = (dispatch, history, params = {}) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["shift-options", params],
    async () => {
      const res = await MasterApi.getMasterShift({
        token,
        ...params,
      });

      return res ?? [];
    },
    {
      // enabled: enabled,
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetMasterShiftById = (dispatch, history, id) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["shift-options", id],
    async () => {
      const res = await MasterApi.getMasterShiftById({
        token,
        id,
      });

      return res?.data ?? [];
    },
    {
      enabled: Boolean(id),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetAllRoom = (dispatch, history, params) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["all-room", params],
    async () => {
      const res = await MasterApi.getAllRoom({
        token,
        ...params,
      });

      return res ?? [];
    },
    {
      // enabled: Boolean(params.projectPerPage > 0),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetRoomOption = (dispatch, history) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["room-option"],
    async () => {
      const res = await MasterApi.getRoomOption({
        token,
      });

      const newData = res?.data.map((item) => ({
        label: item.name,
        value: item.room_id,
      }));

      return newData ?? [];
    },
    {
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useGetTableByRoomCode = (dispatch, history, room_code) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["table-by-room-code", room_code],
    async () => {
      const res = await MasterApi.getTableByRoomCode({
        token,
        room_code,
      });

      return res?.data ?? [];
    },
    {
      enabled: Boolean(room_code),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};

export const useImportRoomTable = (dispatch, history, refetch, closeModal) => {
  return useMutation(MasterApi.importRoomTable, {
    onMutate: () => showLoading(),
    onSuccess: () => {
      Swal("Berhasil mengimport data ruangan dan meja", "", "success");
      refetch();
      hideLoading();
      closeModal();
    },
    onError: (err) => {
      ResponseError(err, dispatch, history);
    },
  });
};

export const useGetMenu = (
  dispatch,
  history,
  role_id,
  outlet_id,
  employee_id,
) => {
  const token = useSelector((state) => state.user.profile.token);
  return useQuery(
    ["get-menu", role_id, outlet_id, employee_id],
    async () => {
      const res = await MasterApi.getMenu({
        token,
        role_id,
        outlet_id,
        employee_id,
      });

      return res?.data ?? [];
    },
    {
      enabled: Boolean(role_id) && Boolean(outlet_id),
      onError: (err) => ResponseError(err, dispatch, history),
    },
  );
};
